import { Fragment, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { renderHeaderLabel, renderTableCell } from "../cellRenderers";
import { MUTED, tableHeaders } from "../constants";
import { exportTablePng } from "../exportUtils";
import { SectionTitle } from "./SectionTitle";

const DEFAULT_CHILD_COLUMN = { key: "company", label: "Child Company" };

export function StateCompanyBreakdownTable({
  title,
  icon,
  rulesKey,
  stateColumn,
  rows,
  isLoading,
  childColumn = DEFAULT_CHILD_COLUMN,
  childrenKey = "childCompanies",
}) {
  const sectionRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const handleExport = async () => {
    if (isLoading) return;
    setIsExporting(true);
    try { await exportTablePng(sectionRef, title); }
    finally { setIsExporting(false); }
  };
  const toggleState = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const columnCount = 1 + tableHeaders.length;

  return (
    <section ref={sectionRef}>
      <SectionTitle title={title} icon={icon} rulesKey={rulesKey} onExport={handleExport} isExporting={isExporting} disabled={isLoading} />
      <div className="overflow-hidden rounded-2xl border border-[#d6dfeb] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1800px] divide-y divide-[#cfd8e6]">
            <thead className="bg-[#233f66] text-white">
              <tr>
                <th className="sticky left-0 z-10 bg-[#233f66] whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-[0.08em]">
                  {stateColumn.label} / {childColumn.label}
                </th>
                {tableHeaders.map((header) => (
                  <th
                    key={header.key}
                    className={`whitespace-nowrap px-4 py-3 text-right text-xs font-black uppercase tracking-[0.08em] ${header.muted ? "text-[#c6d2e5]" : "text-white"}`}
                  >
                    {renderHeaderLabel(header.label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#d6dfeb] text-sm text-[#24385b]">
              {isLoading ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-8 text-center font-semibold text-[#7085a5]">
                    Loading data...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row, index) => {
                  const stateLabel = row[stateColumn.key] || "—";
                  const stateKey = row[stateColumn.key] || `row-${index}`;
                  const children = Array.isArray(row[childrenKey]) ? row[childrenKey] : [];
                  const canExpand = children.length > 0;
                  const isExpanded = canExpand && expandedKeys.has(stateKey);
                  return (
                    <Fragment key={`${stateKey}-${index}`}>
                      <tr className="odd:bg-white even:bg-[#f6f8fc]">
                        <td className="sticky left-0 z-10 bg-inherit whitespace-nowrap px-4 py-4 font-black">
                          <button
                            type="button"
                            onClick={() => canExpand && toggleState(stateKey)}
                            className={`flex items-center gap-2 ${canExpand ? "" : "cursor-default"}`}
                            disabled={!canExpand}
                          >
                            {canExpand && (
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className="text-[10px] transition-transform"
                                style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)", color: MUTED }}
                              />
                            )}
                            <span>{stateLabel}</span>
                          </button>
                        </td>
                        {tableHeaders.map((header) => (
                          <td key={header.key} className="whitespace-nowrap px-4 py-4 text-right font-semibold">
                            {renderTableCell(header.key, row[header.key])}
                          </td>
                        ))}
                      </tr>
                      {canExpand && isExpanded && children.map((child, childIndex) => (
                        <tr key={`${stateKey}-child-${childIndex}`} className="bg-[#fafbfd]">
                          <td className="sticky left-0 z-10 bg-inherit whitespace-nowrap px-4 py-3 pl-10 font-semibold text-[#3a577f]">
                            {child[childColumn.key] || "—"}
                          </td>
                          {tableHeaders.map((header) => (
                            <td key={header.key} className="whitespace-nowrap px-4 py-3 text-right text-[#3a577f]">
                              {renderTableCell(header.key, child[header.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-8 text-center font-semibold text-[#7085a5]">
                    No data available for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
